import { Homework, StudentHomeworkStatus, TutorLog, HomeworkTask } from '../types';

export interface TaskBrainScore {
  level: number;       // 0: Pure Brain, 1: Nudge, 2: Scaffold, 3: Deep support
  score: number;       // 100, 80, 50, 25
  hintsUsed: number;
  label: string;       // e.g. "100% Brain Power • No AI Used"
  shortTag: string;    // e.g. "100% Brain (0 AI)"
  badgeColor: 'emerald' | 'blue' | 'amber' | 'purple';
  bgClass: string;
  textClass: string;
  borderClass: string;
  icon: string;
}

export interface DayBrainMetrics {
  totalTasks: number;
  completedTasks: number;
  pureBrainTasks: number; // 0 hints used
  hintsUsedTotal: number;
  averageScore: number;   // 0 to 100
  allPureBrain: boolean;  // completed > 0 && pureBrainTasks === completedTasks
  summaryTag: string;     // e.g. "100% Brain Power • No AI"
}

export type BrainTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface BrainPowerBadge {
  id: string;
  tier: BrainTier | 'special';
  name: string;
  explicitSubtitle: string;
  description: string;
  icon: string;
  color: {
    bg: string;
    text: string;
    border: string;
    hex: string;
  };
  requiredPureDays: number;
}

export const BRAIN_POWER_BADGES: BrainPowerBadge[] = [
  {
    id: 'brain-bronze',
    tier: 'bronze',
    name: 'Bronze Brain Scholar',
    explicitSubtitle: '100% Brain Power • 1st Day No AI',
    description: 'Completed a full daily homework assignment using 100% human thought with 0 AI hints.',
    icon: 'psychology',
    color: {
      bg: 'bg-amber-50',
      text: 'text-amber-800',
      border: 'border-amber-400',
      hex: '#b45309',
    },
    requiredPureDays: 1,
  },
  {
    id: 'brain-silver',
    tier: 'silver',
    name: 'Silver Brain Navigator',
    explicitSubtitle: '100% Brain Power • 3-Day Pure Brain Streak (0 AI)',
    description: 'Solved 3 consecutive homework days with 100% Brain Power and Zero AI assistance.',
    icon: 'military_tech',
    color: {
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      border: 'border-slate-400',
      hex: '#64748b',
    },
    requiredPureDays: 3,
  },
  {
    id: 'brain-gold',
    tier: 'gold',
    name: 'Gold Master Thinker',
    explicitSubtitle: '100% Brain Power • 7-Day Pure Brain Streak (0 AI)',
    description: '7 consecutive homework days conquered with pure human intellect and 0 AI prompts.',
    icon: 'workspace_premium',
    color: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      border: 'border-yellow-400',
      hex: '#d97706',
    },
    requiredPureDays: 7,
  },
  {
    id: 'brain-platinum',
    tier: 'platinum',
    name: 'Platinum Genius Tier',
    explicitSubtitle: '100% Brain Power • 30-Day Pure Brain Master (0 AI)',
    description: 'Elite 30-day streak of pure independent learning with zero AI shortcuts.',
    icon: 'diamond',
    color: {
      bg: 'bg-cyan-50',
      text: 'text-cyan-700',
      border: 'border-cyan-400',
      hex: '#0891b2',
    },
    requiredPureDays: 30,
  },
  {
    id: 'brain-mindful-explorer',
    tier: 'special',
    name: 'Smart AI Explorer',
    explicitSubtitle: 'More Brain, Less AI • Mindful Nudge Only',
    description: 'Used AI ethically as a micro-nudge (max 1 hint) and solved the problem independently.',
    icon: 'emoji_objects',
    color: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-400',
      hex: '#059669',
    },
    requiredPureDays: 0,
  },
];

/**
 * Computes the Brain Score and Level for a specific homework task based on AI Tutor interaction count.
 * Level 0: 0 hints  -> 100% Brain (No AI)
 * Level 1: 1 hint   -> 80% Brain (Quick Nudge)
 * Level 2: 2 hints  -> 50% Brain (Guided Practice)
 * Level 3: 3+ hints -> 25% Brain (Deep Support)
 */
export function getTaskBrainScore(tutorLog?: TutorLog | null): TaskBrainScore {
  const hintsCount = tutorLog?.interactions ? tutorLog.interactions.length : 0;

  if (hintsCount === 0) {
    return {
      level: 0,
      score: 100,
      hintsUsed: 0,
      label: '100% Brain Power • No AI Used',
      shortTag: '100% Brain (0 AI)',
      badgeColor: 'emerald',
      bgClass: 'bg-emerald-50',
      textClass: 'text-emerald-700',
      borderClass: 'border-emerald-300',
      icon: 'psychology',
    };
  }

  if (hintsCount === 1) {
    return {
      level: 1,
      score: 80,
      hintsUsed: 1,
      label: 'Quick Spark • 1 Hint Used (80% Brain Power)',
      shortTag: '80% Brain (1 Hint)',
      badgeColor: 'blue',
      bgClass: 'bg-blue-50',
      textClass: 'text-blue-700',
      borderClass: 'border-blue-300',
      icon: 'lightbulb',
    };
  }

  if (hintsCount === 2) {
    return {
      level: 2,
      score: 50,
      hintsUsed: 2,
      label: 'Guided Practice • 2 Hints Used (50% Brain Power)',
      shortTag: '50% Brain (2 Hints)',
      badgeColor: 'amber',
      bgClass: 'bg-amber-50',
      textClass: 'text-amber-700',
      borderClass: 'border-amber-300',
      icon: 'handshake',
    };
  }

  return {
    level: 3,
    score: 25,
    hintsUsed: hintsCount,
    label: `Deep Support • ${hintsCount} Hints Used (25% Brain Power)`,
    shortTag: `25% Brain (${hintsCount} Hints)`,
    badgeColor: 'purple',
    bgClass: 'bg-purple-50',
    textClass: 'text-purple-700',
    borderClass: 'border-purple-300',
    icon: 'school',
  };
}

/**
 * Computes daily metrics for a specific homework assignment
 */
export function computeDayBrainMetrics(
  homework: Homework | undefined,
  studentStatus: StudentHomeworkStatus | undefined,
  tutorLogs: TutorLog[],
  studentId: string
): DayBrainMetrics {
  if (!homework || !homework.tasks || homework.tasks.length === 0) {
    return {
      totalTasks: 0,
      completedTasks: 0,
      pureBrainTasks: 0,
      hintsUsedTotal: 0,
      averageScore: 100,
      allPureBrain: false,
      summaryTag: 'No Tasks',
    };
  }

  const completedTaskIds = studentStatus?.completedTaskIds || [];
  let totalScore = 0;
  let pureBrainCount = 0;
  let totalHints = 0;

  homework.tasks.forEach((task) => {
    const isCompleted = completedTaskIds.includes(task.id);
    const log = tutorLogs.find(
      (tl) => tl.studentId === studentId && tl.homeworkId === homework.id && tl.taskId === task.id
    );
    const taskScore = getTaskBrainScore(log);

    if (isCompleted) {
      totalScore += taskScore.score;
      totalHints += taskScore.hintsUsed;
      if (taskScore.hintsUsed === 0) {
        pureBrainCount++;
      }
    }
  });

  const completedCount = completedTaskIds.length;
  const averageScore = completedCount > 0 ? Math.round(totalScore / completedCount) : 100;
  const allPureBrain = completedCount > 0 && pureBrainCount === completedCount;

  let summaryTag = '100% Brain Power • No AI Used';
  if (completedCount === 0) {
    summaryTag = 'Pending Homework';
  } else if (allPureBrain) {
    summaryTag = '100% Brain Power • 0 AI Used';
  } else if (averageScore >= 80) {
    summaryTag = `${averageScore}% Brain Power • More Brain, Less AI`;
  } else {
    summaryTag = `${averageScore}% Brain Power • Assisted by Tutor`;
  }

  return {
    totalTasks: homework.tasks.length,
    completedTasks: completedCount,
    pureBrainTasks: pureBrainCount,
    hintsUsedTotal: totalHints,
    averageScore,
    allPureBrain,
    summaryTag,
  };
}

export interface StudentBrainPowerProfile {
  totalCompletedTasks: number;
  pureBrainTasks: number;
  assistedTasks: number;
  averageLifetimeScore: number;
  currentPureBrainStreak: number;
  bestPureBrainStreak: number;
  totalPureBrainDays: number;
  currentTier: BrainTier | 'none';
  highestUnlockedBadge: BrainPowerBadge | null;
  unlockedBadges: BrainPowerBadge[];
  nextBadge: BrainPowerBadge | null;
  progressToNext: number; // 0 to 100
  nextMilestoneDescription: string;
}

/**
 * Calculates a student's full historical Brain Power profile across all homeworks
 */
export function computeStudentBrainPowerProfile(
  studentId: string,
  allHomeworks: Homework[],
  studentHomeworkStatuses: StudentHomeworkStatus[],
  tutorLogs: TutorLog[]
): StudentBrainPowerProfile {
  // Sort homeworks chronologically
  const sortedHomeworks = [...allHomeworks]
    .filter((h) => h.tasks && h.tasks.length > 0)
    .sort((a, b) => a.date.localeCompare(b.date));

  let totalCompletedTasks = 0;
  let pureBrainTasks = 0;
  let assistedTasks = 0;
  let totalScoreSum = 0;

  // Track per-day status for streaks
  const dayResults: { date: string; isFullPureDay: boolean; hasCompletedAll: boolean }[] = [];

  sortedHomeworks.forEach((hw) => {
    const status = studentHomeworkStatuses.find(
      (s) => s.studentId === studentId && s.homeworkId === hw.id
    );
    const completedIds = status?.completedTaskIds || [];
    if (completedIds.length === 0) return;

    let dayPureCount = 0;
    hw.tasks.forEach((task) => {
      if (completedIds.includes(task.id)) {
        totalCompletedTasks++;
        const log = tutorLogs.find(
          (tl) => tl.studentId === studentId && tl.homeworkId === hw.id && tl.taskId === task.id
        );
        const score = getTaskBrainScore(log);
        totalScoreSum += score.score;

        if (score.hintsUsed === 0) {
          pureBrainTasks++;
          dayPureCount++;
        } else {
          assistedTasks++;
        }
      }
    });

    const hasCompletedAll = completedIds.length === hw.tasks.length;
    const isFullPureDay = hasCompletedAll && dayPureCount === hw.tasks.length;
    dayResults.push({ date: hw.date, isFullPureDay, hasCompletedAll });
  });

  // Calculate streaks
  let currentStreak = 0;
  let bestStreak = 0;
  let totalPureBrainDays = 0;

  // Streak runs backwards from most recent day
  for (let i = dayResults.length - 1; i >= 0; i--) {
    if (dayResults[i].isFullPureDay) {
      currentStreak++;
    } else {
      break;
    }
  }

  // Best historical streak & total pure days
  let tempStreak = 0;
  dayResults.forEach((d) => {
    if (d.isFullPureDay) {
      totalPureBrainDays++;
      tempStreak++;
      if (tempStreak > bestStreak) bestStreak = tempStreak;
    } else {
      tempStreak = 0;
    }
  });

  const averageLifetimeScore =
    totalCompletedTasks > 0 ? Math.round(totalScoreSum / totalCompletedTasks) : 100;

  // Unlocked badges based on pure brain days or streaks
  const effectiveDays = Math.max(bestStreak, totalPureBrainDays);

  const unlockedBadges: BrainPowerBadge[] = [];
  const bronze = BRAIN_POWER_BADGES.find((b) => b.id === 'brain-bronze')!;
  const silver = BRAIN_POWER_BADGES.find((b) => b.id === 'brain-silver')!;
  const gold = BRAIN_POWER_BADGES.find((b) => b.id === 'brain-gold')!;
  const platinum = BRAIN_POWER_BADGES.find((b) => b.id === 'brain-platinum')!;
  const mindful = BRAIN_POWER_BADGES.find((b) => b.id === 'brain-mindful-explorer')!;

  if (effectiveDays >= 1) unlockedBadges.push(bronze);
  if (effectiveDays >= 3) unlockedBadges.push(silver);
  if (effectiveDays >= 7) unlockedBadges.push(gold);
  if (effectiveDays >= 30) unlockedBadges.push(platinum);
  if (assistedTasks > 0 && averageLifetimeScore >= 75) unlockedBadges.push(mindful);

  let currentTier: BrainTier | 'none' = 'none';
  let highestBadge: BrainPowerBadge | null = null;
  let nextBadge: BrainPowerBadge | null = bronze;
  let progressToNext = 0;
  let nextMilestoneDescription = 'Complete 1 full day of homework with 0 AI to unlock Bronze!';

  if (effectiveDays >= 30) {
    currentTier = 'platinum';
    highestBadge = platinum;
    nextBadge = null;
    progressToNext = 100;
    nextMilestoneDescription = 'You reached the ultimate Platinum Genius tier!';
  } else if (effectiveDays >= 7) {
    currentTier = 'gold';
    highestBadge = gold;
    nextBadge = platinum;
    progressToNext = Math.min(100, Math.round(((effectiveDays - 7) / (30 - 7)) * 100));
    nextMilestoneDescription = `${30 - effectiveDays} more days with 0 AI to reach Platinum!`;
  } else if (effectiveDays >= 3) {
    currentTier = 'silver';
    highestBadge = silver;
    nextBadge = gold;
    progressToNext = Math.min(100, Math.round(((effectiveDays - 3) / (7 - 3)) * 100));
    nextMilestoneDescription = `${7 - effectiveDays} more days with 0 AI to reach Gold!`;
  } else if (effectiveDays >= 1) {
    currentTier = 'bronze';
    highestBadge = bronze;
    nextBadge = silver;
    progressToNext = Math.min(100, Math.round(((effectiveDays - 1) / (3 - 1)) * 100));
    nextMilestoneDescription = `${3 - effectiveDays} more days with 0 AI to reach Silver!`;
  } else {
    currentTier = 'none';
    highestBadge = null;
    nextBadge = bronze;
    progressToNext = 0;
    nextMilestoneDescription = 'Complete 1 full day of homework with 0 AI to unlock Bronze!';
  }

  return {
    totalCompletedTasks,
    pureBrainTasks,
    assistedTasks,
    averageLifetimeScore,
    currentPureBrainStreak: currentStreak,
    bestPureBrainStreak: bestStreak,
    totalPureBrainDays,
    currentTier,
    highestUnlockedBadge: highestBadge,
    unlockedBadges,
    nextBadge,
    progressToNext,
    nextMilestoneDescription,
  };
}
